import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PageEditorWorkspace } from './PageEditorWorkspace';
import { AttachmentPreview } from './AttachmentPreview';
import { PageAccessManager } from './PageAccessManager';
import { ProtectedHtml } from './ProtectedHtml';
import {
  BookOpen, FileText, FileEdit, Eye, Clock, ShieldCheck, Megaphone, Search, Plus,
  ArrowLeft, History, MessageSquare, Trash2, Send, CheckCircle2, Paperclip, ExternalLink, Maximize2, Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  draft: 'bg-slate-100 text-slate-600',
  review: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  published: 'bg-emerald-100 text-emerald-700',
  archived: 'bg-slate-200 text-slate-500'
};

const StatCard = ({ icon: Icon, label, value, tone = 'slate', onClick, testid }) => (
  <button
    onClick={onClick}
    className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md hover:border-slate-300 transition-all"
    data-testid={testid}
  >
    <div className="flex items-center gap-3">
      <div className={`h-9 w-9 rounded-lg bg-${tone}-100 flex items-center justify-center`}>
        <Icon className={`h-4 w-4 text-${tone}-600`} />
      </div>
      <div>
        <p className="text-xl font-semibold text-slate-800 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  </button>
);

const emptyForm = {
  title: '', body: '', excerpt: '', template: 'sop', department: '', category: '',
  stage: '', tags: '', owner: '', parent_id: '', is_mandatory: false,
  mandatory_departments: [], status: 'draft', attachments: []
};

export const OfficiumPages = ({ getAuthToken, currentUser, standalone = false }) => {
  const [view, setView] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({ departments: [], categories: [], tags: [], owners: [], stages: [], statuses: [] });
  const [templates, setTemplates] = useState([]);
  const [pages, setPages] = useState([]);
  const [filters, setFilters] = useState({ q: '', department: '', category: '', tag: '', status: '', mandatory_only: false });
  const [openPage, setOpenPage] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState('');
  const [versions, setVersions] = useState([]);
  const [compare, setCompare] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [baseline, setBaseline] = useState(JSON.stringify(emptyForm));
  const [loadError, setLoadError] = useState('');
  const [detailNotice, setDetailNotice] = useState('');
  const [detailBusy, setDetailBusy] = useState(false);
  const detailRequest = useRef(0);
  const initialized = useRef(false);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [previewSource, setPreviewSource] = useState('page');
  const [accessOpen, setAccessOpen] = useState(false);

  const cfg = useCallback(() => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } }), [getAuthToken]);
  const canWrite = meta.can_write;
  const canPublish = meta.can_publish;
  const [pdfBusy, setPdfBusy] = useState('');

  const downloadPdf = async page => {
    setPdfBusy(page.id);
    try {
      const res = await axios.get(`${API}/api/kb/pages/${page.id}/pdf`, { ...cfg(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(page.title || 'officium-page').replace(/[^A-Za-z0-9 _-]+/g, '').trim() || 'officium-page'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      let detail = '';
      try {
        const text = await error.response?.data?.text?.();
        detail = text ? (JSON.parse(text).detail || '') : (error.response?.data?.detail || '');
      } catch { detail = ''; }
      toast.error(error.response?.status === 403
        ? 'Only admins can download pages as PDF'
        : detail || `Could not build the PDF (${error.response?.status || 'network error'}). Please tell the team.`);
    } finally {
      setPdfBusy('');
    }
  };

  const pageURL = (id, edit = false) => `/officium-pages${id ? `?page=${encodeURIComponent(id)}${edit ? '&edit=1' : ''}` : ''}`;
  const updateURL = url => { if (standalone) window.history.replaceState(null, '', url); };
  const errorText = (error, fallback) => {
    if (error.response?.status >= 500) return `${fallback}. The page service is temporarily unavailable. Please try again.`;
    return typeof error.response?.data?.detail === 'string' ? error.response.data.detail : fallback;
  };

  const loadDashboard = useCallback(async () => {
    try {
      const [statsRes, metaRes, tplRes] = await Promise.all([
        axios.get(`${API}/api/kb/dashboard`, cfg()),
        axios.get(`${API}/api/kb/meta`, cfg()),
        axios.get(`${API}/api/kb/templates`, cfg())
      ]);
      setStats(statsRes.data);
      setMeta(metaRes.data);
      setTemplates(tplRes.data.templates || []);
    } catch (error) {
      setLoadError(errorText(error, 'Could not load Officium Pages'));
    }
  }, [cfg]);

  const loadPages = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '' && v !== false));
      const res = await axios.get(`${API}/api/kb/pages`, { ...cfg(), params });
      setPages(res.data.pages || []);
    } catch (error) {
      toast.error(errorText(error, 'Could not load pages'));
    }
  }, [cfg, filters]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { if (view === 'list') loadPages(); }, [view, loadPages]);

  useEffect(() => {
    if (!standalone || initialized.current || !templates.length) return;
    initialized.current = true;
    const query = new URLSearchParams(window.location.search);
    if (query.get('page')) {
      openDetail(query.get('page')).then(page => {
        if (page && query.get('edit') === '1') {
          if (page.can_write) startEdit(page);
          else setLoadError('You have read-only access to this page.');
        }
      });
    } else if (query.get('new') === '1') {
      if (meta.can_write) startCreate(query.get('template') || 'sop');
      else setLoadError('You have read-only access to Officium Pages.');
    }
  }, [standalone, templates, meta.can_write]);

  const openDetail = async (pageId) => {
    const request = ++detailRequest.current;
    setDetailBusy(true);
    setLoadError('');
    setDetailNotice('');
    try {
      const pageRes = await axios.get(`${API}/api/kb/pages/${pageId}`, cfg());
      if (request !== detailRequest.current) return null;
      setOpenPage(pageRes.data);
      setComments([]);
      setVersions([]);
      setCommentBody('');
      setCompare(null);
      setCompliance(null);
      setPreviewIndex(null);
      setPreviewSource('page');
      setView('page');
      setLoadError('');
      updateURL(pageURL(pageId));
      // Comments/history failures must not hide a readable or editable document.
      loadPageExtras(pageId, request);
      return pageRes.data;
    } catch (error) {
      if (request === detailRequest.current) setLoadError(errorText(error, 'Could not open page'));
      return null;
    } finally {
      if (request === detailRequest.current) setDetailBusy(false);
    }
  };

  const loadPageExtras = async (pageId, request) => {
    const results = await Promise.allSettled([
      axios.get(`${API}/api/kb/pages/${pageId}/comments`, cfg()),
      axios.get(`${API}/api/kb/pages/${pageId}/versions`, cfg())
    ]);
    if (request !== detailRequest.current) return;
    const [commentsResult, versionsResult] = results;
    if (commentsResult.status === 'fulfilled') setComments(commentsResult.value.data.comments || []);
    if (versionsResult.status === 'fulfilled') setVersions(versionsResult.value.data.versions || []);
    const failed = ['Comments', 'Version history'].filter((_, index) => results[index].status === 'rejected');
    setDetailNotice(failed.length ? `${failed.join(' and ')} could not load. Your page is still available.` : '');
  };

  const startCreate = (templateId = 'sop') => {
    const tpl = templates.find(t => t.id === templateId);
    setEditingId(null);
    const next = { ...emptyForm, template: tpl?.id || 'sop', body: tpl?.body || '', category: tpl?.category || '' };
    setForm(next);
    setBaseline(JSON.stringify(next));
    setEditorOpen(true);
    updateURL(`/officium-pages?new=1&template=${encodeURIComponent(next.template)}`);
    loadPages();
  };

  const startEdit = (page) => {
    setEditingId(page.id);
    const next = {
      title: page.title || '', body: page.body || '', excerpt: page.excerpt || '',
      template: page.template || 'sop', department: page.department || '', category: page.category || '',
      stage: page.stage || '', tags: (page.tags || []).join(', '), owner: page.owner || '',
      parent_id: page.parent_id || '', is_mandatory: !!page.is_mandatory,
      mandatory_departments: page.mandatory_departments || [], status: page.status || 'draft',
      attachments: page.attachments || []
    };
    setForm(next);
    setBaseline(JSON.stringify(next));
    setEditorOpen(true);
    updateURL(pageURL(page.id, true));
    loadPages();
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setPreviewIndex(null);
    updateURL(pageURL(openPage?.id));
  };

  const savePage = async () => {
    if (busy) return null;
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        parent_id: form.parent_id || null
      };
      let saved;
      if (editingId) {
        delete payload.status;
        const res = await axios.patch(`${API}/api/kb/pages/${editingId}`, payload, cfg());
        toast.success('Page updated');
        saved = res.data;
        setEditorOpen(false);
        await openDetail(res.data.id);
      } else {
        const res = await axios.post(`${API}/api/kb/pages`, payload, cfg());
        toast.success('Page created');
        saved = res.data;
        setEditorOpen(false);
        await openDetail(res.data.id);
      }
      loadDashboard();
      return saved;
    } catch (error) {
      toast.error(errorText(error, 'Could not save page'));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openEditorNewTab = async () => {
    if (!form.title.trim()) { toast.error('Add a title before saving and opening a new tab.'); return; }
    const tab = window.open('about:blank', '_blank');
    if (!tab) { toast.error('Your browser blocked the new tab. Allow popups and try again; your draft is unchanged.'); return; }
    tab.opener = null;
    const saved = await savePage();
    if (saved && !tab.closed) tab.location.replace(pageURL(saved.id, true));
    else if (!tab.closed) tab.close();
  };

  const changeStatus = async (status) => {
    try {
      const res = await axios.post(`${API}/api/kb/pages/${openPage.id}/status`, { status }, cfg());
      setOpenPage(prev => ({ ...prev, ...res.data }));
      toast.success(`Page moved to ${status}`);
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not change status');
    }
  };

  const acknowledge = async () => {
    try {
      await axios.post(`${API}/api/kb/pages/${openPage.id}/acknowledge`, {}, cfg());
      setOpenPage(prev => ({ ...prev, acknowledged: true }));
      toast.success('Acknowledged - thank you');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not acknowledge');
    }
  };

  const addComment = async () => {
    if (!commentBody.trim()) return;
    try {
      const res = await axios.post(`${API}/api/kb/pages/${openPage.id}/comments`, { body: commentBody }, cfg());
      setComments(prev => [...prev, res.data]);
      setCommentBody('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not post comment');
    }
  };

  const showVersion = async (version) => {
    try {
      const res = await axios.get(`${API}/api/kb/pages/${openPage.id}/versions/${version}`, cfg());
      setCompare(res.data);
    } catch (error) {
      toast.error('Could not load version');
    }
  };

  const restoreVersion = async (version) => {
    if (!window.confirm(`Restore version ${version}? The current content is saved to history first.`)) return;
    try {
      await axios.post(`${API}/api/kb/pages/${openPage.id}/versions/${version}/restore`, {}, cfg());
      toast.success(`Restored version ${version}`);
      setCompare(null);
      await openDetail(openPage.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not restore version');
    }
  };

  const loadCompliance = async () => {
    try {
      const res = await axios.get(`${API}/api/kb/pages/${openPage.id}/compliance`, cfg());
      setCompliance(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load compliance report');
    }
  };

  const deletePage = async () => {
    if (!window.confirm(`Delete "${openPage.title}" permanently?`)) return;
    try {
      await axios.delete(`${API}/api/kb/pages/${openPage.id}`, cfg());
      toast.success('Page deleted');
      setView('list');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not delete page');
    }
  };

  const seedStarters = async () => {
    try {
      const res = await axios.post(`${API}/api/kb/seed-starter-pages`, {}, cfg());
      toast.success(res.data.count ? `Created ${res.data.count} starter pages` : 'Starter pages already exist');
      loadDashboard();
      if (view === 'list') loadPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create starter pages');
    }
  };

  const uploadFile = async (file) => {
    const body = new FormData();
    body.append('file', file);
    try {
      const res = await axios.post(`${API}/api/kb/uploads`, body, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not upload ${file.name}`);
      return null;
    }
  };

  const removePageAttachment = async (attachmentId) => {
    try {
      const res = await axios.delete(`${API}/api/kb/pages/${openPage.id}/attachments/${attachmentId}`, cfg());
      setOpenPage(prev => ({ ...prev, attachments: res.data.attachments || [] }));
      toast.success('Attachment removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove attachment');
    }
  };

  const filteredCount = useMemo(() => pages.length, [pages]);

  return (
    <div className="space-y-5 min-w-0" data-testid="officium-pages">
      {accessOpen && <PageAccessManager onClose={() => setAccessOpen(false)} onSaved={() => { loadDashboard(); loadPages(); toast.success('Pages access updated'); }} />}
      {loadError && <div role="alert" data-testid="kb-load-error" className="text-sm text-red-700 border border-red-200 bg-red-50 p-4 rounded-lg">{loadError}</div>}
      {detailBusy && <p role="status" data-testid="kb-page-loading" className="text-sm text-slate-500">Opening page…</p>}
      <div className="bg-gradient-to-r from-slate-50 via-indigo-50/40 to-blue-50/40 rounded-2xl p-6 border border-slate-100 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg shadow-indigo-200/50">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-slate-800">Officium Pages</h2>
              <p className="text-sm text-slate-500 mt-0.5">Knowledge base for recruitment, training, HR policies and SOPs</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canPublish && <Button data-testid="kb-access-management" size="sm" variant="outline" onClick={() => setAccessOpen(true)}>Lock / unlock access</Button>}
            {!standalone && <Button asChild variant="outline" size="sm"><a href={pageURL(view === 'page' ? openPage?.id : null)} data-testid="kb-open-fullscreen" title="Open full-screen workspace"><Maximize2 className="h-4 w-4 mr-1" />Full screen</a></Button>}
            <Button asChild variant="outline" size="sm"><a href={pageURL(view === 'page' ? openPage?.id : null)} target="_blank" rel="noopener noreferrer" data-testid="kb-open-new-tab"><ExternalLink className="h-4 w-4 mr-1" />Open in new tab</a></Button>
            <Button variant={view === 'dashboard' ? 'default' : 'outline'} size="sm" onClick={() => { setView('dashboard'); updateURL(pageURL()); }} data-testid="kb-view-dashboard">Overview</Button>
            <Button variant={view === 'list' ? 'default' : 'outline'} size="sm" onClick={() => { setView('list'); updateURL(pageURL()); }} data-testid="kb-view-list">All Pages</Button>
            {canWrite && (
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => startCreate()} data-testid="kb-create-page">
                <Plus className="h-4 w-4 mr-1" /> New Page
              </Button>
            )}
          </div>
        </div>
      </div>

      {view === 'dashboard' && stats && (
        <div className="space-y-5" data-testid="kb-dashboard">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <StatCard icon={FileText} label="Total pages" value={stats.total_pages} tone="slate" onClick={() => { setFilters(f => ({ ...f, status: '' })); setView('list'); }} testid="kb-stat-total" />
            <StatCard icon={CheckCircle2} label="Published" value={stats.published_pages} tone="emerald" onClick={() => { setFilters(f => ({ ...f, status: 'published' })); setView('list'); }} testid="kb-stat-published" />
            <StatCard icon={FileEdit} label="Drafts" value={stats.draft_pages} tone="amber" onClick={() => { setFilters(f => ({ ...f, status: 'draft' })); setView('list'); }} testid="kb-stat-drafts" />
            <StatCard icon={Clock} label="Pending approvals" value={stats.pending_approvals_count} tone="blue" onClick={() => { setFilters(f => ({ ...f, status: 'review' })); setView('list'); }} testid="kb-stat-pending" />
            <StatCard icon={ShieldCheck} label="Unread mandatory" value={stats.unread_mandatory_count} tone="red" onClick={() => { setFilters(f => ({ ...f, mandatory_only: true, status: 'published' })); setView('list'); }} testid="kb-stat-mandatory" />
            <StatCard icon={Eye} label="Updated this week" value={stats.updated_this_week} tone="indigo" onClick={() => setView('list')} testid="kb-stat-updated" />
          </div>

          {stats.unread_mandatory_count > 0 && (
            <div className="border border-red-200 bg-red-50 rounded-xl p-4" data-testid="kb-mandatory-banner">
              <p className="text-sm font-semibold text-red-700 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" /> {stats.unread_mandatory_count} mandatory {stats.unread_mandatory_count === 1 ? 'policy' : 'policies'} need your acknowledgement
              </p>
              <div className="mt-2 space-y-1">
                {stats.unread_mandatory.map(page => (
                  <button key={page.id} onClick={() => openDetail(page.id)} className="text-sm text-red-700 underline block" data-testid={`kb-unread-${page.id}`}>
                    {page.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[
              { title: 'Most viewed', icon: Eye, items: stats.most_viewed, meta: p => `${p.views || 0} views` },
              { title: 'Recently updated', icon: Clock, items: stats.recently_updated, meta: p => new Date(p.updated_at).toLocaleDateString() },
              { title: 'Announcements', icon: Megaphone, items: stats.recent_announcements, meta: p => p.department || 'All teams' }
            ].map(section => (
              <div key={section.title} className="bg-white border border-slate-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
                  <section.icon className="h-4 w-4 text-slate-400" /> {section.title}
                </h3>
                {section.items?.length ? (
                  <div className="space-y-2">
                    {section.items.map(page => (
                      <button key={page.id} onClick={() => openDetail(page.id)} className="w-full text-left group" data-testid={`kb-link-${page.id}`}>
                        <p className="text-sm text-slate-700 group-hover:text-indigo-600 truncate">{page.title}</p>
                        <p className="text-xs text-slate-400">{section.meta(page)}</p>
                      </button>
                    ))}
                  </div>
                ) : <p className="text-xs text-slate-400">Nothing here yet</p>}
              </div>
            ))}
          </div>

          {canWrite && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Start from a template</h3>
              <div className="flex flex-wrap gap-2">
                {templates.map(tpl => (
                  <Button key={tpl.id} variant="outline" size="sm" onClick={() => startCreate(tpl.id)} data-testid={`kb-template-${tpl.id}`}>
                    {tpl.name}
                  </Button>
                ))}
                {canPublish && (
                  <Button variant="ghost" size="sm" onClick={seedStarters} data-testid="kb-seed-starters">
                    Create recruitment stage SOPs
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {view === 'list' && (
        <div className="space-y-4" data-testid="kb-list">
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search pages by title, tag or content..."
                value={filters.q}
                onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
                data-testid="kb-search-input"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                { key: 'department', label: 'Department', all: 'All departments', options: meta.departments },
                { key: 'category', label: 'Category', all: 'All categories', options: meta.categories },
                { key: 'tag', label: 'Tag', all: 'All tags', options: meta.tags },
                { key: 'status', label: 'Status', all: 'All statuses', options: meta.statuses },
                { key: 'owner', label: 'Owner', all: 'All owners', options: meta.owners }
              ].map(filter => (
                <Select
                  key={filter.key}
                  value={filters[filter.key] || 'all'}
                  onValueChange={(v) => setFilters(f => ({ ...f, [filter.key]: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger data-testid={`kb-filter-${filter.key}`}><SelectValue placeholder={filter.label} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{filter.all}</SelectItem>
                    {(filter.options || []).map(option => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                  </SelectContent>
                </Select>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Switch checked={filters.mandatory_only} onCheckedChange={(v) => setFilters(f => ({ ...f, mandatory_only: v }))} data-testid="kb-filter-mandatory" />
                Mandatory reads only
              </label>
              <span className="text-xs text-slate-400">{filteredCount} pages</span>
            </div>
          </div>

          {pages.length === 0 ? (
            <div className="border border-dashed border-slate-200 rounded-xl p-10 text-center">
              <BookOpen className="h-8 w-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No pages match your filters yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white">
              {pages.map(page => (
                <div key={page.id} className="px-4 py-3 hover:bg-slate-50 flex items-start justify-between gap-3">
                  <button onClick={() => openDetail(page.id)} className="flex-1 min-w-0 text-left" data-testid={`kb-row-${page.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-800 truncate">{page.title}</span>
                        <Badge className={`text-[10px] rounded-md ${STATUS_STYLE[page.status] || STATUS_STYLE.draft}`}>{page.status}</Badge>
                        {page.is_mandatory && <Badge className="bg-red-100 text-red-700 text-[10px] rounded-md">Mandatory</Badge>}
                        {page.stage && <Badge className="bg-indigo-50 text-indigo-700 text-[10px] rounded-md">{page.stage}</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {[page.department, page.category, (page.tags || []).join(', ')].filter(Boolean).join(' · ') || 'Uncategorised'}
                      </p>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-slate-400">{page.views || 0} views</span>
                    {canPublish && (
                      <Button variant="ghost" size="sm" className="h-7 px-2" title="Download this page as PDF"
                        disabled={pdfBusy === page.id} onClick={() => downloadPdf(page)} data-testid={`kb-row-pdf-${page.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'page' && openPage && (
        <div className="space-y-4" data-testid="kb-page-view">
          {detailNotice && <div role="alert" data-testid="kb-detail-notice" className="flex flex-wrap items-center justify-between gap-2 text-sm text-amber-800 border border-amber-200 bg-amber-50 p-3 rounded-lg">
            <span data-testid="kb-detail-notice-text">{detailNotice}</span>
            <Button variant="outline" size="sm" data-testid="kb-retry-details" onClick={() => loadPageExtras(openPage.id, detailRequest.current)}>Retry</Button>
          </div>}
          <Button variant="ghost" size="sm" onClick={() => { setView('list'); updateURL(pageURL()); }} data-testid="kb-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to pages
          </Button>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 data-testid="kb-page-title" className="text-xl font-bold text-slate-800 [overflow-wrap:anywhere]">{openPage.title}</h1>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <Badge className={`text-[10px] rounded-md ${STATUS_STYLE[openPage.status]}`}>{openPage.status}</Badge>
                  <span className="text-xs text-slate-500">v{openPage.version} · {openPage.views} views · owner {openPage.owner}</span>
                  {openPage.is_mandatory && <Badge className="bg-red-100 text-red-700 text-[10px] rounded-md">Mandatory read</Badge>}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {[openPage.department, openPage.category, openPage.stage].filter(Boolean).join(' · ')}
                  {openPage.tags?.length ? ` · ${openPage.tags.join(', ')}` : ''}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {openPage.can_publish && <Button size="sm" variant="outline" disabled={pdfBusy === openPage.id} onClick={() => downloadPdf(openPage)} data-testid="kb-download-pdf-btn"><Download className="h-3.5 w-3.5 mr-1" />{pdfBusy === openPage.id ? 'Preparing…' : 'PDF'}</Button>}
                {openPage.can_write && <Button size="sm" variant="outline" onClick={() => startEdit(openPage)} data-testid="kb-edit-btn">Edit</Button>}
                {openPage.can_write && openPage.status === 'draft' && <Button size="sm" variant="outline" onClick={() => changeStatus('review')} data-testid="kb-submit-review-btn"><Send className="h-3.5 w-3.5 mr-1" />Submit for review</Button>}
                {openPage.can_publish && openPage.status === 'review' && <Button size="sm" variant="outline" onClick={() => changeStatus('approved')} data-testid="kb-approve-btn">Approve</Button>}
                {openPage.can_publish && ['approved', 'review', 'archived'].includes(openPage.status) && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => changeStatus('published')} data-testid="kb-publish-btn">Publish</Button>}
                {openPage.can_publish && openPage.status === 'published' && <Button size="sm" variant="outline" onClick={() => changeStatus('archived')} data-testid="kb-archive-btn">Archive</Button>}
                {openPage.can_publish && <Button size="sm" variant="outline" className="text-red-600" onClick={deletePage} data-testid="kb-delete-btn"><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>

            {openPage.is_mandatory && openPage.status === 'published' && (
              <div className={`mt-4 rounded-lg p-3 border ${openPage.acknowledged ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                {openPage.acknowledged ? (
                  <p className="text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> You have acknowledged this policy</p>
                ) : (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-sm text-amber-800">This is a mandatory read. Please confirm once you have read it.</p>
                    <Button size="sm" onClick={acknowledge} data-testid="kb-acknowledge-btn">I have read and understood</Button>
                  </div>
                )}
              </div>
            )}

            <ProtectedHtml className="kb-content mt-5 text-sm text-slate-700" html={openPage.body || '<p>This page is empty.</p>'} data-testid="kb-page-body" />

            {openPage.attachments?.length > 0 && (
              <div className="mt-5 border-t border-slate-100 pt-4">
                <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2">Attachments</h3>
                <div className="flex flex-wrap gap-2">
                  {openPage.attachments.map((att, idx) => (
                    <span key={att.id || idx} className="flex items-center gap-2 text-xs bg-slate-100 px-2 py-1 rounded-lg" data-testid={`kb-attachment-${att.id || idx}`}>
                      <button
                        onClick={() => { setPreviewSource('page'); setPreviewIndex(idx); }}
                        disabled={busy || detailBusy}
                        className="flex items-center gap-1 hover:underline"
                        title="Preview without leaving the page"
                        data-testid={`kb-preview-attachment-${att.id || idx}`}
                      >
                        <Paperclip className="h-3 w-3 text-slate-400" />
                        {att.filename || att.url}
                        {att.size ? <span className="text-slate-400">({Math.max(1, Math.round(att.size / 1024))} KB)</span> : null}
                      </button>
                      {openPage.can_write && att.id && (
                        <button onClick={() => removePageAttachment(att.id)} className="text-red-500" data-testid={`kb-detach-${att.id}`}>×</button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3"><MessageSquare className="h-4 w-4 text-slate-400" /> Comments ({comments.length})</h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {comments.map(comment => (
                  <div key={comment.id} className="text-sm" data-testid={`kb-comment-${comment.id}`}>
                    <p className="text-xs text-slate-500">{comment.author_name} · {new Date(comment.created_at).toLocaleString()}</p>
                    <p className="text-slate-700 whitespace-pre-wrap">{comment.body}</p>
                  </div>
                ))}
                {comments.length === 0 && <p className="text-xs text-slate-400">No comments yet</p>}
              </div>
              <div className="mt-3 space-y-2">
                <Textarea rows={2} placeholder="Add a comment. Mention a teammate with @their.email@domain" value={commentBody} onChange={(e) => setCommentBody(e.target.value)} data-testid="kb-comment-input" />
                <Button size="sm" onClick={addComment} data-testid="kb-comment-submit">Comment</Button>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3"><History className="h-4 w-4 text-slate-400" /> Version history</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {versions.map(version => (
                  <div key={version.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`kb-version-${version.version}`}>
                    <div className="min-w-0">
                      <p className="text-slate-700">v{version.version} · {version.edited_by}</p>
                      <p className="text-xs text-slate-400 truncate">{version.change_note} · {new Date(version.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => showVersion(version.version)} data-testid={`kb-compare-${version.version}`}>Compare</Button>
                      {openPage.can_write && <Button size="sm" variant="ghost" onClick={() => restoreVersion(version.version)} data-testid={`kb-restore-${version.version}`}>Restore</Button>}
                    </div>
                  </div>
                ))}
                {versions.length === 0 && <p className="text-xs text-slate-400">No history yet</p>}
              </div>
              {openPage.can_write && openPage.is_mandatory && (
                <Button size="sm" variant="outline" className="mt-3" onClick={loadCompliance} data-testid="kb-compliance-btn">
                  <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Compliance report
                </Button>
              )}
            </div>
          </div>

          {compare && (
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="kb-compare-view">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700">Comparing v{compare.version.version} with current v{compare.current.version}</h3>
                <Button size="sm" variant="ghost" onClick={() => setCompare(null)}>Close</Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-slate-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-slate-500 mb-2">v{compare.version.version} — {compare.version.title}</p>
                  <ProtectedHtml className="kb-content text-xs" html={compare.version.body} />
                </div>
                <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/30">
                  <p className="text-xs font-semibold text-emerald-700 mb-2">Current v{compare.current.version} — {compare.current.title}</p>
                  <ProtectedHtml className="kb-content text-xs" html={compare.current.body} />
                </div>
              </div>
            </div>
          )}

          {compliance && (
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="kb-compliance-report">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700">
                  Acknowledgement: {compliance.acknowledged}/{compliance.total} ({compliance.percentage}%)
                </h3>
                <Button size="sm" variant="ghost" onClick={() => setCompliance(null)}>Close</Button>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                {compliance.rows.map(row => (
                  <div key={row.email} className="flex items-center justify-between py-2 text-sm gap-3">
                    <span className="text-slate-700 min-w-0 truncate">
                      {row.name}
                      <span className="text-xs text-slate-400"> · {row.email}{row.department ? ` · ${row.department}` : ''}</span>
                    </span>
                    {row.acknowledged
                      ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px] rounded-md shrink-0">Acknowledged {row.acknowledged_at ? new Date(row.acknowledged_at).toLocaleDateString() : ''}</Badge>
                      : <Badge className="bg-amber-100 text-amber-700 text-[10px] rounded-md shrink-0">Pending</Badge>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <AttachmentPreview
        attachments={previewSource === 'form' ? (form.attachments || []) : (openPage?.attachments || [])}
        index={previewIndex}
        onClose={() => setPreviewIndex(null)}
        onNavigate={setPreviewIndex}
      />

      {editorOpen && <PageEditorWorkspace open onClose={closeEditor} form={form} setForm={setForm}
        editingId={editingId} dirty={JSON.stringify(form) !== baseline} busy={busy} onSave={savePage}
        onNewTab={openEditorNewTab} meta={meta} templates={templates} pages={pages} uploadFile={uploadFile}
        onPreviewAttachment={index => { setPreviewSource('form'); setPreviewIndex(index); }} />}
    </div>
  );
};

export default OfficiumPages;
